<script setup lang="ts">

import { Direction } from '../fin/optimisation';
import MontecarloRetirementOutput from './MontecarloRetirementOutput.vue';
import { ref, Ref } from 'vue';
import { RetirementConfig } from '../fin/retirement_config';
import RetirementConfigElem from './RetirementConfigElem.vue';
import { MultipleHistory } from '../fin/retirement';
import { TimeSeries } from '../fin/history';
import { Quantiles, STANDARD_QUANTILES } from '../fin/quantile';
import * as wt from '../worker/types.ts';
import * as wc from '../worker/client.ts';

interface Loadable {
  load(): void;
}

const retirementConfigElemRef = ref<Loadable|null>(null);
const montecarloRetirementOutputRef = ref<InstanceType<typeof MontecarloRetirementOutput>|null>(null);
const earlyPensionWithdrawalGuidance = ref<Direction>(Direction.NO_GUIDANCE);
const postRetirementIsaSavingGuidance = ref<Direction>(Direction.NO_GUIDANCE);
let latestConfig: Ref<RetirementConfig|void> = ref<RetirementConfig|void>();
const exhausted: Ref<string> = ref<string>('');
const exhaustionYear: Ref<string> = ref<string>('');
const exhaustionAge: Ref<string> = ref<string>('');

let savingsQuantilesTS: Ref<TimeSeries<Quantiles>> = ref(new TimeSeries<Quantiles>('ALL_SAVINGS', []));
let birthYear: Ref<number> = ref<number>(0);
const retirementYear: Ref<number> = ref<number>(0);
const maxYear: Ref<number> = ref<number>(0);
const calculationCompletionRatio: Ref<number> = ref<number>(0);
let currentCalculationTask: wt.TaskCreationResponse | undefined = undefined;
const emit = defineEmits(['configChanged']);

async function calculate(config: RetirementConfig): Promise<void> {
  console.log('starting MontecarloRetirement.vue calculate()');
  latestConfig.value = config;
  retirementConfigElemRef.value?.load();
  currentCalculationTask?.cancelTask();

  console.assert(
    config.market.taxed_growth_rate.stddev > 0.01 &&
    config.market.untaxed_growth_rate.stddev > 0.01,
    'MontecarloRetirement.vue config has insufficient volatility of return.',
  );

  currentCalculationTask = wc.startTask(
    wt.WorkerAction.ParallelMontecarloRetirement,
    JSON.stringify(config.serialize()),
    (progress: number) => {
      console.debug(`got worker task progress message: ${progress}`);
      calculationCompletionRatio.value = progress;
    },
  );

  try {
    const serializedResult = await currentCalculationTask.resultPromise;
    const atqs = MultipleHistory.deserialize(serializedResult);
    savingsQuantilesTS.value = atqs.historyQuantiles('ALL_SAVINGS');
    birthYear.value = config.person.birth_date.getUTCFullYear();
    retirementYear.value = config.person.retirement_date.getUTCFullYear();
    maxYear.value = atqs.maxYear;
    calculationCompletionRatio.value = 1.0;

    const exhaustionYears: number[] = atqs.exhaustionYears;

    const exhaustionYearQuantiles = new Quantiles(
      STANDARD_QUANTILES,
      exhaustionYears);

    const exhaustionYearNum = exhaustionYearQuantiles.get(
      config.person.critical_quantile_key) ?? 0;

    const exhaustedBool
      = exhaustionYearNum < config.person.max_age + maxYear.value;

    const exhaustionAgeNum = exhaustedBool ?
      exhaustionYearNum - birthYear.value :
      0;

    if (exhaustedBool) {
      exhausted.value = "yes";
      exhaustionYear.value = exhaustionYearNum.toString();
      exhaustionAge.value = exhaustionAgeNum.toString();
    } else {
      exhausted.value = "no";
      exhaustionYear.value = '';
      exhaustionAge.value = '';
    }
  } catch (ex) {
    console.warn(`got worker task failure message: ${ex}`);
  }
}

function redraw(): void {
  if (montecarloRetirementOutputRef.value) {
    montecarloRetirementOutputRef.value.redraw();
  }
}

defineExpose({
  calculate,
  redraw,
});

</script>

<template>
  <div activityName="Montecarlo Retirement">
    <h2>Montecarlo Retirement</h2>
    <div class="explainer-card">
      Explore multiple retirements using randomly varying market conditions
      according to configurable statistical market properties, so as to
      consider the likelihood of running out of money early.
      <br/>
      Such ensemble simulations offer a way to explore sequencing risk: the
      chance that an unlucky few years at the beginning could have
      disproportionate impact on the rest of your retirement.
    </div>
    <RetirementConfigElem
      ref="retirementConfigElemRef"
      :quantileKeys="[...STANDARD_QUANTILES]"
      @configChanged="(cfg) => {calculate(cfg); emit('configChanged', cfg);}"
      :showMarketUncertainty="true"
      :earlyPensionWithdrawalGuidance="earlyPensionWithdrawalGuidance"
      :postRetirementIsaSavingGuidance="postRetirementIsaSavingGuidance"
    />
    <div>
      <table>
        <tbody>
          <tr v-on:click="() => calculate(latestConfig!)">
            <td>Calculation progress</td>
            <td>{{Math.round(calculationCompletionRatio * 100)}}%</td>
          </tr>
        </tbody>
      </table>
    </div>
    <MontecarloRetirementOutput
      ref="montecarloRetirementOutputRef"
      :calculationCompletionRatio="calculationCompletionRatio"
      :savingsQuantilesTS="savingsQuantilesTS"
      :criticalQuantileKey="latestConfig?.person.critical_quantile_key ?? 0.05"
      :birthYear="birthYear"
      :retirementYear="retirementYear"
      :maxYear="maxYear"
      :exhausted="exhausted"
      :exhaustionYear="exhaustionYear"
      :exhaustionAge="exhaustionAge"
    />
  </div>
</template>

<style scoped>
</style>
